/**
 * Pure Relay game logic — ported 1:1 from firebase/functions/src/relayGameLogic.ts.
 * The whole game is deterministic from the room's `startedAt`, so the client
 * derives phases locally and stays in lockstep with the server without extra
 * reads. KEEP IN SYNC with the server file.
 */
import type { TriviaQuestion } from "./types";

// ---- Timing (must sum to the 15-minute Relay) ----
export const QUESTION_MS = 30_000;
export const QUESTIONS_PER_ROUND = 5;
export const ROUNDS = 4;
export const TOTAL_QUESTIONS = QUESTIONS_PER_ROUND * ROUNDS; // 20
export const ROUND_MS = QUESTION_MS * QUESTIONS_PER_ROUND; // 2.5 min
export const TRIVIA_MS = ROUND_MS * ROUNDS; // 10 min
export const CHAT_MS = 5 * 60_000; // 5 min
export const DECIDE_MS = 60_000; // 1 min match/pass picks
export const GAME_AND_CHAT_MS = TRIVIA_MS + CHAT_MS; // 15 min
export const RELAY_TOTAL_MS = GAME_AND_CHAT_MS + DECIDE_MS; // 16 min
export const FISHBOWL_MSG_LIMIT = 10;

export type Pair = [string, string];

/** Stable id for a partner pairing, order-independent. */
export function pairId(a: string, b: string): string {
  return [a, b].sort().join("_");
}

/** Mixed room: man i pairs woman (i + round) mod 4 — a Latin square. */
export function mixedRotation(males: string[], females: string[]): Pair[][] {
  const n = males.length;
  const rounds: Pair[][] = [];
  for (let r = 0; r < ROUNDS; r++) {
    const round: Pair[] = [];
    for (let i = 0; i < n; i++) round.push([males[i], females[(i + r) % n]]);
    rounds.push(round);
  }
  return rounds;
}

/** Single-gender room: circle-method round robin. */
export function roundRobinRotation(people: string[], rounds: number = ROUNDS): Pair[][] {
  const n = people.length;
  const fixed = people[0];
  let rotating = people.slice(1);
  const out: Pair[][] = [];
  const maxRounds = Math.min(rounds, n - 1);
  for (let r = 0; r < maxRounds; r++) {
    const round: Pair[] = [[fixed, rotating[0]]];
    for (let i = 1; i < n / 2; i++) {
      round.push([rotating[i], rotating[rotating.length - i]]);
    }
    out.push(round);
    rotating = [rotating[rotating.length - 1], ...rotating.slice(0, rotating.length - 1)];
  }
  return out;
}

export function rotationForRoom(
  participants: string[],
  genders: Record<string, string>,
): Pair[][] {
  const males = participants.filter((u) => genders[u] === "male");
  const females = participants.filter((u) => genders[u] === "female");
  if (males.length === females.length && males.length * 2 === participants.length) {
    return mixedRotation(males, females);
  }
  return roundRobinRotation(participants);
}

/** Your partner (and pair id) for a given round, or null. */
export function partnerFor(
  participants: string[],
  genders: Record<string, string>,
  uid: string,
  round: number,
): { pid: string; partner: string } | null {
  const schedule = rotationForRoom(participants, genders);
  const pair = (schedule[round] ?? []).find((p) => p.includes(uid));
  if (!pair) return null;
  return { pid: pairId(pair[0], pair[1]), partner: pair[0] === uid ? pair[1] : pair[0] };
}

/** Mutual preference match: each seeks the other's gender. */
export function mutualCompat(
  genders: Record<string, string>,
  seekings: Record<string, string[]>,
  a: string,
  b: string,
): boolean {
  return (seekings[a] ?? []).includes(genders[b]) &&
    (seekings[b] ?? []).includes(genders[a]);
}

/**
 * Progressive reveal, GENDER-GATED: identities only ever unlock across the
 * preference line — same-side people stay anonymous for the whole relay (the
 * server enforces this too: your views/{uid} doc simply doesn't carry their
 * names). Within the compatible set, pair games unlock partners round by
 * round; acro (whole-room) and the chat/decide finale unlock everyone
 * compatible.
 */
export function revealedTo(
  me: string,
  participants: string[],
  genders: Record<string, string>,
  seekings: Record<string, string[]>,
  gameType: string,
  startedAt: number,
  now: number,
): Set<string> {
  const compat = new Set(
    participants.filter((p) => p === me || mutualCompat(genders, seekings, me, p)),
  );
  const elapsed = now - startedAt;
  if (elapsed < 0) return new Set([me]);
  if (gameType === "acro") return compat;
  if (elapsed >= TRIVIA_MS) return compat; // chat + decide: all compatible revealed
  const out = new Set<string>([me]);
  const roundsStarted = Math.min(ROUNDS, Math.floor(elapsed / ROUND_MS) + 1);
  const schedule = rotationForRoom(participants, genders);
  for (let r = 0; r < roundsStarted; r++) {
    const pair = (schedule[r] ?? []).find((p) => p.includes(me));
    if (pair) {
      const partner = pair[0] === me ? pair[1] : pair[0];
      if (compat.has(partner)) out.add(partner);
    }
  }
  return out;
}

export type Phase =
  | { phase: "lobby"; startsAt: number }
  | { phase: "trivia"; round: number; questionIndex: number; questionEndsAt: number }
  | { phase: "chat"; endsAt: number }
  | { phase: "decide"; endsAt: number }
  | { phase: "ended" };

export function phaseAt(startedAt: number, now: number): Phase {
  const elapsed = now - startedAt;
  if (elapsed < 0) return { phase: "lobby", startsAt: startedAt };
  if (elapsed < TRIVIA_MS) {
    const questionIndex = Math.floor(elapsed / QUESTION_MS);
    return {
      phase: "trivia",
      round: Math.floor(questionIndex / QUESTIONS_PER_ROUND),
      questionIndex,
      questionEndsAt: startedAt + (questionIndex + 1) * QUESTION_MS,
    };
  }
  if (elapsed < GAME_AND_CHAT_MS) return { phase: "chat", endsAt: startedAt + GAME_AND_CHAT_MS };
  if (elapsed < RELAY_TOTAL_MS) return { phase: "decide", endsAt: startedAt + RELAY_TOTAL_MS };
  return { phase: "ended" };
}

export function isCorrect(q: TriviaQuestion, answer: string | null | undefined): boolean {
  return answer != null && answer === q.correct;
}

/** Final per-person leaderboard: a team's correct answer scores BOTH partners. */
export function leaderboard(
  participants: string[],
  schedule: Pair[][],
  questions: TriviaQuestion[],
  answers: Record<number, Record<string, string>>,
): Record<string, number> {
  const score: Record<string, number> = {};
  for (const uid of participants) score[uid] = 0;
  for (let q = 0; q < questions.length; q++) {
    const round = Math.floor(q / QUESTIONS_PER_ROUND);
    const pairs = schedule[round] ?? [];
    for (const [a, b] of pairs) {
      const ans = answers[q]?.[pairId(a, b)];
      if (isCorrect(questions[q], ans)) {
        score[a] = (score[a] ?? 0) + 1;
        score[b] = (score[b] ?? 0) + 1;
      }
    }
  }
  return score;
}

// ---- Headbandz ----
export const HB_HALF_MS = ROUND_MS / 2;

export function headbandzGiver(pair: Pair, msIntoRound: number): string {
  return msIntoRound < HB_HALF_MS ? pair[0] : pair[1];
}

export function isSingleWordClue(clue: string): boolean {
  const t = clue.trim();
  return t.length > 0 && !/\s/.test(t);
}

/** A clue gives the game away if it IS the secret word (or contains it —
 * "snowmans", "thesnowman"). Looser matches like "snow" for "snowman" are
 * fair play. Mirrors the server-side check. */
export function clueRevealsWord(clue: string, word: string): boolean {
  const norm = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
  const w = norm(word);
  return w.length > 0 && norm(clue).includes(w);
}

// ---- Funny Sentences (acro) ----
export const ACRO_ROUNDS = 5;
export const ACRO_WRITE_MS = 60_000;
export const ACRO_REVEAL_MS_EACH = 5_000;
export const ACRO_VOTE_MS = 10_000;
export const ACRO_REVEAL_MS = 8 * ACRO_REVEAL_MS_EACH;
export const ACRO_ROUND_MS = ACRO_WRITE_MS + ACRO_REVEAL_MS + ACRO_VOTE_MS;
export const ACRO_GAME_MS = ACRO_ROUNDS * ACRO_ROUND_MS;

export type AcroPhase =
  | { phase: "lobby"; startsAt: number }
  | { phase: "write" | "reveal" | "vote"; round: number; endsAt: number }
  | { phase: "chat"; endsAt: number }
  | { phase: "decide"; endsAt: number }
  | { phase: "ended" };

export function acroPhaseAt(startedAt: number, now: number): AcroPhase {
  const elapsed = now - startedAt;
  if (elapsed < 0) return { phase: "lobby", startsAt: startedAt };
  if (elapsed >= RELAY_TOTAL_MS) return { phase: "ended" };
  if (elapsed >= GAME_AND_CHAT_MS) return { phase: "decide", endsAt: startedAt + RELAY_TOTAL_MS };
  if (elapsed >= ACRO_GAME_MS) return { phase: "chat", endsAt: startedAt + GAME_AND_CHAT_MS };
  const round = Math.floor(Math.max(0, elapsed) / ACRO_ROUND_MS);
  const roundStart = startedAt + round * ACRO_ROUND_MS;
  const into = now - roundStart;
  if (into < ACRO_WRITE_MS) return { phase: "write", round, endsAt: roundStart + ACRO_WRITE_MS };
  if (into < ACRO_WRITE_MS + ACRO_REVEAL_MS) {
    return { phase: "reveal", round, endsAt: roundStart + ACRO_WRITE_MS + ACRO_REVEAL_MS };
  }
  return { phase: "vote", round, endsAt: roundStart + ACRO_ROUND_MS };
}

export function validateAcrostic(sentence: string, letters: string[]): boolean {
  const words = sentence.trim().split(/\s+/).filter(Boolean);
  if (words.length !== letters.length) return false;
  return words.every((w, i) => {
    const first = (w.match(/[a-z]/i) ?? [])[0]?.toLowerCase();
    return first != null && first === letters[i].toLowerCase();
  });
}

/** Cheap 32-bit string hash (FNV-1a) — used to derive a well-mixed shuffle seed. */
function hashStr(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Deterministic option order so all clients show the same layout.
 * The seed is derived from the question's own text (identical on every client)
 * mixed with the position — NOT the raw question index. Seeding directly with a
 * small sequential index (0–4) made mulberry32 land the correct answer at the
 * same slot every time (it was always option C). Hashing the content spreads it.
 */
export function shuffledOptions(q: TriviaQuestion, seed: number): string[] {
  const opts = [q.correct, ...q.incorrect];
  const rand = mulberry32((hashStr(q.id + "|" + q.question) ^ Math.imul(seed + 1, 0x9e3779b1)) >>> 0);
  for (let i = opts.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [opts[i], opts[j]] = [opts[j], opts[i]];
  }
  return opts;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** mm:ss for countdowns. */
export function fmtClock(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Duel (1v1) timing — mirrors functions/src/duel.ts constants exactly.
// ---------------------------------------------------------------------------
export const DUEL_TRIVIA_QUESTIONS = 5;
export const DUEL_QUESTION_MS = 15_000;
export const DUEL_TRIVIA_MS = DUEL_TRIVIA_QUESTIONS * DUEL_QUESTION_MS; // 75s
export const DUEL_HB_HALF_MS = 90_000; // keep in sync with functions/duel.ts
export const DUEL_HB_MS = DUEL_HB_HALF_MS * 2; // 180s
export const DUEL_ACRO_ROUNDS = 3;
export const DUEL_ACRO_WRITE_MS = 45_000;
export const DUEL_ACRO_REVEAL_MS = 7_000;
export const DUEL_ACRO_ROUND_MS = DUEL_ACRO_WRITE_MS + DUEL_ACRO_REVEAL_MS;
export const DUEL_ACRO_MS = DUEL_ACRO_ROUNDS * DUEL_ACRO_ROUND_MS;

export function duelGameMs(gameType: string): number {
  if (gameType === "headbandz") return DUEL_HB_MS;
  if (gameType === "acro") return DUEL_ACRO_MS;
  return DUEL_TRIVIA_MS;
}

export type DuelPhase =
  | { phase: "lobby"; startsAt: number }
  | { phase: "trivia"; questionIndex: number; questionEndsAt: number }
  | { phase: "hb"; half: number; halfEndsAt: number }
  | { phase: "acroWrite" | "acroReveal"; round: number; endsAt: number }
  | { phase: "decision" };

/** Where a duel is right now, from its clock. */
export function duelPhaseAt(gameType: string, startedAt: number, now: number): DuelPhase {
  const elapsed = now - startedAt;
  if (elapsed < 0) return { phase: "lobby", startsAt: startedAt };
  if (elapsed >= duelGameMs(gameType)) return { phase: "decision" };
  if (gameType === "headbandz") {
    const half = Math.min(Math.floor(elapsed / DUEL_HB_HALF_MS), 1);
    return { phase: "hb", half, halfEndsAt: startedAt + (half + 1) * DUEL_HB_HALF_MS };
  }
  if (gameType === "acro") {
    const round = Math.min(Math.floor(elapsed / DUEL_ACRO_ROUND_MS), DUEL_ACRO_ROUNDS - 1);
    const into = elapsed - round * DUEL_ACRO_ROUND_MS;
    return into < DUEL_ACRO_WRITE_MS
      ? { phase: "acroWrite", round, endsAt: startedAt + round * DUEL_ACRO_ROUND_MS + DUEL_ACRO_WRITE_MS }
      : { phase: "acroReveal", round, endsAt: startedAt + (round + 1) * DUEL_ACRO_ROUND_MS };
  }
  const questionIndex = Math.min(Math.floor(elapsed / DUEL_QUESTION_MS), DUEL_TRIVIA_QUESTIONS - 1);
  return { phase: "trivia", questionIndex, questionEndsAt: startedAt + (questionIndex + 1) * DUEL_QUESTION_MS };
}
